/**
 * BMI Calculator Custom Element
 * Handles real-time calculation and display updates
 */
class BMICalculatorElement extends HTMLElement {
  constructor() {
    super();
    
    // Bind methods to maintain proper context
    this.updateWeightDisplay = this.updateWeightDisplay.bind(this);
    this.updateHeightDisplay = this.updateHeightDisplay.bind(this);
    this.calculateAndUpdate = this.calculateAndUpdate.bind(this);
    this.toggleBenefits = this.toggleBenefits.bind(this);
  }

  connectedCallback() {
    // Read metric setting from data attribute
    this.useMetric = this.dataset.useMetric === 'true';
    
    this.cacheElements();
    this.loadSettings();
    this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  cacheElements() {
    this.weightInput = this.querySelector('[data-input="weight"]');
    this.heightInput = this.querySelector('[data-input="height"]');
    
    // Find weight and height value displays in their new locations
    const weightInputGroup = this.weightInput?.closest('.bmi-calculator__input-group');
    const heightInputGroup = this.heightInput?.closest('.bmi-calculator__input-group');
    
    this.weightValue = weightInputGroup?.querySelector('.bmi-calculator__value-number');
    this.weightUnit = weightInputGroup?.querySelector('.bmi-calculator__value-unit');
    this.heightValue = heightInputGroup?.querySelector('.bmi-calculator__value-number');
    this.heightUnit = heightInputGroup?.querySelector('.bmi-calculator__value-unit');
    
    this.bmiValue = this.querySelector('.bmi-calculator__bmi-value');
    this.resultNumber = this.querySelector('.bmi-calculator__result-number');
    this.resultUnit = this.querySelector('.bmi-calculator__result-unit');
    this.viewMoreBtn = this.querySelector('.bmi-calculator__view-more');
    this.benefitsSection = this.querySelector('.bmi-calculator__additional-benefits');
    this.benefitsText = this.querySelector('.bmi-calculator__benefits-text');
  }

  loadSettings() {
    // Load custom messages and benefits from JSON script
    const settingsScript = this.querySelector('.bmi-calculator__settings');
    if (settingsScript) {
      try {
        this.settings = JSON.parse(settingsScript.textContent);
      } catch (e) {
        console.warn('Failed to parse BMI calculator settings:', e);
        this.settings = this.getDefaultSettings();
      }
    } else {
      this.settings = this.getDefaultSettings();
    }
  }

  getDefaultSettings() {
    return {
      messages: {
        normal: "Based on your input you could lose up to",
        overweight: "Based on your input, you could lose up to",
        underweight: "Based on your input, we do not recommend use of MB-1 for weight loss, however..."
      },
      benefits: {
        normal: "While MB-1 can be an effective tool for weight management, it can also provide you with some other incredible health benefits including steady energy levels, boosted daily caloric burn, craving control, and optimized gut health.",
        overweight: "MB-1 can also provide you with steady energy levels, optimized daily caloric burn and craving control, balanced blood sugar & cholesterol levels and improved gut health.",
        underweight: "MB-1 can provide you with some incredible health benefits including steady energy levels, control cravings, regulate appetite, and optimize gut health."
      },
      timePeriod: "in 3 months"
    };
  }

  init() {
    // Debug: Check if all elements are found
    if (!this.weightInput || !this.heightInput) {
      console.warn('BMI Calculator: Input elements not found');
      return;
    }
    
    if (!this.weightValue || !this.heightValue) {
      console.warn('BMI Calculator: Value display elements not found');
      return;
    }
    
    // Set initial values and add event listeners
    this.updateDisplays();
    this.addEventListeners();
  }

  addEventListeners() {
    // Remove existing listeners to prevent duplicates
    this.weightInput?.removeEventListener('input', this.updateWeightDisplay);
    this.weightInput?.removeEventListener('input', this.calculateAndUpdate);
    this.heightInput?.removeEventListener('input', this.updateHeightDisplay);
    this.heightInput?.removeEventListener('input', this.calculateAndUpdate);
    this.viewMoreBtn?.removeEventListener('click', this.toggleBenefits);

    // Weight slider change
    this.weightInput?.addEventListener('input', this.updateWeightDisplay);
    this.weightInput?.addEventListener('input', this.calculateAndUpdate);

    // Height slider change
    this.heightInput?.addEventListener('input', this.updateHeightDisplay);
    this.heightInput?.addEventListener('input', this.calculateAndUpdate);

    // View More button
    this.viewMoreBtn?.addEventListener('click', this.toggleBenefits);
  }

  toggleBenefits() {
    if (this.benefitsSection && this.viewMoreBtn) {
      const isVisible = this.benefitsSection.style.display !== 'none';
      
      if (isVisible) {
        this.benefitsSection.style.display = 'none';
        this.viewMoreBtn.style.display = 'inline';
      } else {
        this.benefitsSection.style.display = 'block';
        this.viewMoreBtn.style.display = 'none';
      }
    }
  }

  /**
   * Convert pounds to kilograms
   */
  lbsToKg(lbs) {
    return lbs * 0.453592;
  }

  /**
   * Convert kilograms to pounds
   */
  kgToLbs(kg) {
    return kg * 2.20462;
  }

  /**
   * Convert centimeters to inches
   */
  cmToInches(cm) {
    return cm / 2.54;
  }

  /**
   * Convert inches to centimeters
   */
  inchesToCm(inches) {
    return inches * 2.54;
  }

  /**
   * Convert meters to inches (for BMI calculation)
   */
  metersToInches(meters) {
    return meters * 39.3701;
  }

  /**
   * Convert inches to meters
   */
  inchesToMeters(inches) {
    return inches / 39.3701;
  }

  updateWeightDisplay() {
    if (this.weightValue && this.weightInput) {
      this.weightValue.textContent = this.weightInput.value;
      this.updateSliderBackground(this.weightInput);
    } else {
      console.warn('BMI Calculator: Weight display elements missing');
    }
  }

  updateHeightDisplay() {
    if (this.heightValue && this.heightInput) {
      if (this.useMetric) {
        // Display in meters (convert from centimeters)
        const cm = parseInt(this.heightInput.value);
        const meters = (cm / 100).toFixed(2);
        this.heightValue.textContent = meters;
        // Show and update unit
        if (this.heightUnit) {
          this.heightUnit.textContent = 'm';
          this.heightUnit.style.display = '';
        }
      } else {
        // Display in feet and inches
        const totalInches = parseInt(this.heightInput.value);
        const feet = Math.floor(totalInches / 12);
        const inches = totalInches % 12;
        this.heightValue.textContent = `${feet}ft ${inches}in`;
        // Hide unit element (not needed for imperial)
        if (this.heightUnit) {
          this.heightUnit.style.display = 'none';
        }
      }
      this.updateSliderBackground(this.heightInput);
    } else {
      console.warn('BMI Calculator: Height display elements missing');
    }
  }

  updateSliderBackground(slider) {
    // Calculate percentage of slider progress
    const min = parseFloat(slider.min);
    const max = parseFloat(slider.max);
    const value = parseFloat(slider.value);
    const percentage = ((value - min) / (max - min)) * 100;
    
    // Update background gradient to show progress
    const containerStyle = getComputedStyle(this);
    const fillColor = containerStyle.getPropertyValue('--progress-fill-color').trim() || '#333333';
    const trackColor = containerStyle.getPropertyValue('--progress-track-color').trim() || '#e0e0e0';
    
    slider.style.background = `linear-gradient(
      to right, 
      ${fillColor} 0%, 
      ${fillColor} ${percentage}%, 
      ${trackColor} ${percentage}%, 
      ${trackColor} 100%
    )`;
  }

  calculateBMI() {
    let weight = parseFloat(this.weightInput?.value || (this.useMetric ? 91 : 200));
    let heightInches = parseFloat(this.heightInput?.value || (this.useMetric ? 165 : 74));
    
    // Convert weight to LBS for BMI calculation if in metric
    if (this.useMetric) {
      weight = this.kgToLbs(weight);
      // Convert height from centimeters to inches for BMI calculation
      heightInches = this.cmToInches(heightInches);
    }
    
    // BMI formula: (weight in pounds × 703) ÷ (height in inches)²
    const bmi = (weight * 703) / (heightInches * heightInches);
    
    return Math.round(bmi * 10) / 10; // Round to 1 decimal place
  }

  calculateWeightLoss(bmi, weightLbs) {
    // Weight loss calculation based on BMI ranges and research
    let calcValue = 0.05; // Default multiplier
    
    // BMI-based calculation values
    if (bmi >= 30) {
      calcValue = 0.12;
    } else if (bmi >= 29) {
      calcValue = 0.09;
    } else if (bmi >= 28) {
      calcValue = 0.08;
    } else if (bmi >= 27) {
      calcValue = 0.07;
    } else if (bmi >= 26) {
      calcValue = 0.06;
    } else if (bmi >= 25) {
      calcValue = 0.05;
    } else if (bmi >= 18.5) {
      // Normal BMI range - fixed 5 lbs
      return 5;
    } else {
      // Below normal BMI - not recommended for weight loss
      return 0;
    }
    
    // Calculate potential loss: weight × multiplier
    const potentialLoss = weightLbs * calcValue;
    
    return Math.round(potentialLoss);
  }

  getResultMessage(bmi, weightLoss) {
    // Return appropriate message based on BMI range using custom settings
    if (bmi < 18.5) {
      return {
        prefix: this.settings.messages.underweight,
        showWeight: false,
        suffix: "",
        benefitsType: "underweight"
      };
    } else if (bmi >= 18.5 && bmi < 25) {
      return {
        prefix: this.settings.messages.normal,
        showWeight: true,
        suffix: this.settings.timePeriod,
        benefitsType: "normal"
      };
    } else {
      return {
        prefix: this.settings.messages.overweight,
        showWeight: true,
        suffix: this.settings.timePeriod,
        benefitsType: "overweight"
      };
    }
  }

  updateResultDisplay(bmi, weightLoss) {
    const message = this.getResultMessage(bmi, weightLoss);
    const resultText = this.querySelector('.bmi-calculator__result-text');
    const resultWeight = this.querySelector('.bmi-calculator__result-weight');
    const resultSuffix = this.querySelector('.bmi-calculator__result-suffix');
    
    // Update prefix text
    if (resultText) {
      resultText.textContent = message.prefix;
    }
    
    // Update weight display
    if (resultWeight) {
      if (message.showWeight && weightLoss > 0) {
        resultWeight.style.display = 'block';
        // Convert weight loss to kg if in metric mode
        const displayWeight = this.useMetric ? Math.round(this.lbsToKg(weightLoss)) : weightLoss;
        this.resultNumber.textContent = displayWeight;
      } else {
        resultWeight.style.display = 'none';
      }
    }
    
    // Update suffix text
    if (resultSuffix) {
      resultSuffix.textContent = message.suffix;
      resultSuffix.style.display = message.suffix ? 'block' : 'none';
    }
    
    // Update benefits text
    if (this.benefitsText && this.settings.benefits[message.benefitsType]) {
      this.benefitsText.textContent = this.settings.benefits[message.benefitsType];
    }
    
    // Reset View More button state
    if (this.viewMoreBtn && this.benefitsSection) {
      this.benefitsSection.style.display = 'none';
      this.viewMoreBtn.style.display = 'inline';
    }
  }

  calculateAndUpdate() {
    const bmi = this.calculateBMI();
    let weight = parseFloat(this.weightInput?.value || (this.useMetric ? 91 : 200));
    
    // Convert to LBS for weight loss calculation if needed
    const weightLbs = this.useMetric ? this.kgToLbs(weight) : weight;
    const weightLoss = this.calculateWeightLoss(bmi, weightLbs);
    
    // Update BMI display
    if (this.bmiValue) {
      this.bmiValue.textContent = Math.round(bmi);
    }
    
    // Update result display with messaging logic
    this.updateResultDisplay(bmi, weightLoss);
  }

  updateDisplays() {
    this.updateWeightDisplay();
    this.updateHeightDisplay();
    this.calculateAndUpdate();
    
    // Initialize slider backgrounds
    if (this.weightInput) this.updateSliderBackground(this.weightInput);
    if (this.heightInput) this.updateSliderBackground(this.heightInput);
  }

  /**
   * Cleanup method for removing event listeners
   */
  cleanup() {
    // Remove weight slider event listeners
    this.weightInput?.removeEventListener('input', this.updateWeightDisplay);
    this.weightInput?.removeEventListener('input', this.calculateAndUpdate);
    
    // Remove height slider event listeners  
    this.heightInput?.removeEventListener('input', this.updateHeightDisplay);
    this.heightInput?.removeEventListener('input', this.calculateAndUpdate);
    
    // Remove view more button event listener
    this.viewMoreBtn?.removeEventListener('click', this.toggleBenefits);
  }

  /**
   * Public API methods
   */
  
  /**
   * Get current BMI value
   */
  get currentBMI() {
    return this.calculateBMI();
  }

  /**
   * Get current weight loss estimation
   */
  get currentWeightLoss() {
    const bmi = this.calculateBMI();
    const weightLbs = parseFloat(this.weightInput?.value || 200);
    return this.calculateWeightLoss(bmi, weightLbs);
  }

  /**
   * Set weight value programmatically
   */
  setWeight(value) {
    if (this.weightInput) {
      this.weightInput.value = value;
      this.updateWeightDisplay();
      this.calculateAndUpdate();
    }
  }

  /**
   * Set height value programmatically  
   */
  setHeight(value) {
    if (this.heightInput) {
      this.heightInput.value = value;
      this.updateHeightDisplay();
      this.calculateAndUpdate();
    }
  }
}

// Register the custom element
if (!customElements.get('bmi-calculator')) {
  customElements.define('bmi-calculator', BMICalculatorElement);
}

// Legacy support - convert existing elements to custom elements
function upgradeExistingBMICalculatorElements() {
  const legacyElements = document.querySelectorAll('.bmi-calculator__form:not(bmi-calculator)');
  legacyElements.forEach(element => {
    // Create new custom element
    const customElement = document.createElement('bmi-calculator');
    
    // Copy attributes
    Array.from(element.attributes).forEach(attr => {
      customElement.setAttribute(attr.name, attr.value);
    });
    
    // Copy classes and maintain them
    customElement.className = element.className;
    
    // Move content
    customElement.innerHTML = element.innerHTML;
    
    // Replace in DOM
    element.parentNode.replaceChild(customElement, element);
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', upgradeExistingBMICalculatorElements);
} else {
  upgradeExistingBMICalculatorElements();
}

// Handle Shopify section events for theme editor
document.addEventListener('shopify:section:load', function(event) {
  setTimeout(() => {
    const legacyElements = event.target.querySelectorAll('.bmi-calculator__form:not(bmi-calculator)');
    legacyElements.forEach(element => {
      const customElement = document.createElement('bmi-calculator');
      Array.from(element.attributes).forEach(attr => {
        customElement.setAttribute(attr.name, attr.value);
      });
      customElement.className = element.className;
      customElement.innerHTML = element.innerHTML;
      element.parentNode.replaceChild(customElement, element);
    });
  }, 0);
});

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = BMICalculatorElement;
}
